import { WatchPatch } from '@core/models/plan-edit.model';

import type { ImpactRound, ImpactSnapshot, PlanWatchState } from './plan-edit.state';
import {
  continuesLiveSession,
  removedRouteUids,
  upsertedRouteUids
} from './plan-watch-overlay';

/**
 * The changes of the session the watch shows, measured whoever makes them: one change runs from the
 * first new revision to the next settled state, however many revisions arrive meanwhile.
 */
export interface WatchImpactState {
  planId: string | null;
  /** Last settled watch state; null until one is seen. */
  settled: ImpactSnapshot | null;
  /** The state the change in progress is measured from; null between changes. */
  baseline: ImpactSnapshot | null;
  /** The change in progress has neither a settled state nor the watch's findings to start from. */
  baselineFindingsStored: boolean;
  touchedRouteUids: string[];
  /**
   * A reconnect cut a change short and this tab may have missed some of its revisions, so the
   * change that runs on to the next settled state is not measured.
   */
  unmeasuredUntilSettled: boolean;
  lastRound: ImpactRound | null;
}

export const initialWatchImpactState: WatchImpactState = {
  planId: null,
  settled: null,
  baseline: null,
  baselineFindingsStored: false,
  touchedRouteUids: [],
  unmeasuredUntilSettled: false,
  lastRound: null
};

const isSettled = (watch: PlanWatchState): boolean =>
  !watch.recomputePending && !watch.recomputeFailed;

const snapshotOf = (watch: PlanWatchState): ImpactSnapshot => ({
  findings: watch.findings ?? [],
  planKpis: watch.planKpis
});

export function watchImpactOnPatch(
  impact: WatchImpactState,
  before: PlanWatchState,
  after: PlanWatchState,
  patch: WatchPatch
): WatchImpactState {
  // A patch buffered for the snapshot is counted by the snapshot, not as a change.
  if (after === before || after.awaitingSnapshot) return impact;
  let next = impact;
  if (after.revision > before.revision) {
    const starts = next.baseline === null;
    next = {
      ...next,
      // With nothing settled yet, the watch before the change is the best measure there is; what it
      // lacks, KPIs or findings, the panel reads from the stored plan.
      baseline: next.baseline ?? next.settled ?? snapshotOf(before),
      baselineFindingsStored: starts
        ? next.settled === null && before.findings === null
        : next.baselineFindingsStored,
      touchedRouteUids: [
        ...new Set([
          ...next.touchedRouteUids,
          ...upsertedRouteUids(patch),
          ...removedRouteUids(patch)
        ])
      ]
    };
  }
  if (!isSettled(after)) return next;
  // A settled state that carries no findings leaves the last settled ones in place, as the
  // session does; reading it as no findings would mark every one of them gone.
  const settled = {
    ...snapshotOf(after),
    findings: after.findings ?? next.settled?.findings ?? []
  };
  if (next.unmeasuredUntilSettled) {
    return {
      ...next,
      settled,
      baseline: null,
      baselineFindingsStored: false,
      touchedRouteUids: [],
      unmeasuredUntilSettled: false
    };
  }
  if (next.baseline === null) return { ...next, settled };
  return {
    ...next,
    settled,
    baseline: null,
    baselineFindingsStored: false,
    touchedRouteUids: [],
    lastRound: {
      before: next.baseline,
      after: settled,
      touchedRouteUids: next.touchedRouteUids,
      ...(next.baselineFindingsStored ? { storedBefore: true as const } : {})
    }
  };
}

/**
 * A snapshot is not a change: re-sent on reconnect it may land mid-change, and the change it cuts
 * short goes unmeasured rather than be diffed across states this tab may have missed. That
 * change runs on until the session settles, so any edit made before then goes unmeasured with it.
 */
export function watchImpactOnSnapshot(
  impact: WatchImpactState,
  before: PlanWatchState,
  after: PlanWatchState
): WatchImpactState {
  const continues =
    after.holder !== null && continuesLiveSession(before, after.holder, after.revision);
  const base: WatchImpactState = continues
    ? {
        ...impact,
        baseline: null,
        baselineFindingsStored: false,
        touchedRouteUids: [],
        unmeasuredUntilSettled: false
      }
    : { ...initialWatchImpactState, planId: impact.planId };
  if (after.phase !== 'live' || !isSettled(after)) {
    return continues ? { ...base, settled: null, unmeasuredUntilSettled: true } : base;
  }
  // A snapshot that carries no findings must not settle on none: the next change would mark every
  // finding new. Without findings to keep, nothing is settled, so that change reads the stored ones.
  const findings = after.findings ?? base.settled?.findings;
  return findings === undefined
    ? base
    : { ...base, settled: { findings, planKpis: after.planKpis } };
}

export function watchImpactOnRecomputeFailed(impact: WatchImpactState): WatchImpactState {
  return { ...impact, baseline: null, baselineFindingsStored: false, touchedRouteUids: [] };
}
